


/**
 * manage/manage-session — the credential vault, the exchange, and the two-stage
 * authority state machine.
 *
 * WHAT THIS FILE IS FOR. Docket 0029 §"The token in the path" and §"Two levels
 * of authority" are protocol, not screen work: one exchange, tokens that live in
 * memory and nowhere else, a step-up that is triggered by the SERVER's refusal
 * rather than by the page guessing, and mutation envelopes keyed per user intent.
 * All of that lives here so no component holds a credential or decides an
 * authority question.
 *
 * ⛔ THE VAULT IS A MODULE CLOSURE, NOT REACT STATE. Session and elevated tokens
 * are never put in `useState`, never in a `data-*` attribute, never in a query
 * string, never logged, never in `localStorage`/`sessionStorage`. React state is
 * inspectable in a devtools panel and serialisable by any error reporter that
 * walks the tree; a module-local `let` is neither. Components receive BOOLEANS
 * and DERIVED FACTS (`elevated`, `scopes`) — never the strings.
 *
 * ⛔ THE PATH SEGMENT STAYS IN THE ADDRESS BAR, AND IS STILL NOT PROOF OF ANYONE.
 * Since JAI-815 (addendum 0038) the segment is the BOOKING ID rather than an
 * opaque token: it is printed in the customer's own email, on their own device.
 * It used to be erased within one paint of the exchange (`history.replaceState`)
 * to keep it out of a `Referer`, a bookmark and a screen-share. That erase is
 * GONE as of 0049, for two reasons and one cost:
 *   - `Referer` is closed by a header we serve on this surface
 *     (`referrer-policy: no-referrer`, alongside `cache-control: no-store`,
 *     `frame-ancestors 'none'` and a `connect-src 'self'` CSP). A server-enforced
 *     rule is the right place for it, and the client erase added nothing to it.
 *   - a bookmark or a history entry is not a leak we are preventing: the address
 *     bar is strictly less exposed than the inbox the link arrived in.
 *   - the cost was every reload, every "open in a new tab", every restored
 *     session and every hand-off at a desk — each of them landed on the no-link
 *     screen with the booking one keystroke out of reach.
 * Nothing else about the vault changed: session and elevation tokens are still
 * memory-only, and the segment is still never treated as proof of identity —
 * every high-impact call goes through `run()`, which honours the server's
 * `403 verificationRequired`. The segment is NOT put into `sessionStorage`
 * either; a re-exchange after rotation reads it from the vault, and the URL is
 * the durable copy it is seeded from.
 *
 * ⚠️ ONE CONTRACT SEAM IS MISSING AND THE PAGE COMPENSATES RATHER THAN PRETENDS.
 * Docket 0029 §"One response header the page must honour" requires reading
 * `X-Manage-Session-Refresh` off the reschedule response. The typed client's
 * manage wrappers return the PARSED BODY — `apiRaw` never exposes `Response`
 * headers — so a page built on the typed client (which is the rule, not a
 * preference) cannot see that header at all. Rather than open a second, untyped
 * transport, this module:
 *   1. renders the `view` the mutation returned (which the docket also requires),
 *      so no call is issued in the window where the old token is already dead;
 *   2. on a KNOWN successful move (the only thing that rotates the generation
 *      from this page), drops the session, the elevation and the scopes at once
 *      (`sessionRotated`), BEFORE any further call can go out on them. The page
 *      keeps the moved view and its notice, and offers the ordinary typed
 *      re-exchange from the booking-id segment as an explicit action
 *      (`recover`). Nothing is exchanged behind the customer's back, and no
 *      business call is replayed;
 *   3. classifies the next `401 sessionExpired` / `sessionRequired` /
 *      `tokenSuperseded` from ANY call (`run`, `reload`) as session loss rather
 *      than death — credentials dropped, phase `'stale'`, the same explicit
 *      re-exchange offered. `tokenSuperseded` is what the worker's
 *      `requireSession` answers when the session's generation is older than the
 *      booking's, i.e. another tab or a move this tab never heard the answer to;
 *      a booking-id link is stable for the life of the booking (0038), so the
 *      re-exchange is the right way back;
 *   4. binds every asynchronous answer to TWO things: the AUTHORITY it was asked
 *      under (the vault `epoch` when it was sent) and the hook LIFETIME that
 *      asked (a fresh identity per effect setup, withdrawn on cleanup, so a
 *      StrictMode remount is a different lifetime). Exchanges additionally carry
 *      a ticket that `pagehide` and any newer exchange withdraw. A late answer
 *      for an authority or a lifetime that no longer holds — a `403
 *      verificationRequired`, a step-up request or confirmation, a view, a
 *      re-exchange — opens nothing, resolves nothing, installs nothing, clears
 *      nothing and replays nothing;
 *   5. binds every CALLBACK it hands out to the GRANT it was rendered with (a
 *      fresh identity per installed session, withdrawn when that session is
 *      dropped, the link dies or the hook is cleaned up) AND to the authority
 *      that hook installed in the shared vault. A component that kept an older
 *      render's `applyView`, `sessionRotated`, `run`, `reload` or step-up wiring
 *      finds it inert once a newer session has been installed — by this hook or
 *      by any other hook sharing the vault: it cannot overwrite the newer view,
 *      drop the newer session, dispatch on it, or answer its step-up. A
 *      successful server answer is still returned to that caller intact; only
 *      its effect on a newer session is withheld;
 *   6. on `pagehide`, a page that was holding a session moves to `'stale'` at
 *      once (its view, and a known move's cause, kept), so a page restored from
 *      the back/forward cache shows the explicit way back instead of controls
 *      that can no longer send anything. Nothing is exchanged on restore.
 * That is honest and recoverable, but it is NOT the header being honoured. The
 * ask is in the reply: either surface the refreshed token in the mutation BODY,
 * or give the manage wrappers a header-observing seam.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isApiError, manageSurface } from "@v0y4ge/api-client";
import { takeEarlyExchange } from "./manage-early";
import type { ManageElevationScope, ManageViewDto } from "@v0y4ge/api-contracts";

/* ── the vault ───────────────────────────────────────────────────────── */

/** The shape the typed client's manage wrappers take. Built per call, never stored. */
interface Credentials {
  sessionToken: string;
  elevationToken?: string;
}

const vault = {
  /**
   * The path segment (the booking id), as the customer opened it. Held here so a
   * re-exchange after a rotation needs no second read; the URL keeps its own
   * copy, which is what makes a reload work.
   */
  segment: "",
  session: "",
  elevation: "",
  scopes: [] as ManageElevationScope[],
  /**
   * The AUTHORITY counter. Bumped every time a session is installed or dropped
   * (never by an elevation on the same session, which is the same authority
   * stepping up). A call captures it when it is dispatched, and every
   * continuation of that call checks it again: an answer that arrives after the
   * session it was sent with has been replaced (a late `401` or `403` from before
   * a move, a step-up code requested or confirmed for a generation that has since
   * ended, a full view read on the old session) can never wipe, elevate, open a
   * step-up over, or replay itself on the session that replaced it.
   */
  epoch: 0,
};

function credentials(): Credentials {
  return vault.elevation
    ? { sessionToken: vault.session, elevationToken: vault.elevation }
    : { sessionToken: vault.session };
}

/** Drop the session, the elevation and the scopes; keep the segment. */
function dropCredentials(): void {
  vault.session = "";
  vault.elevation = "";
  vault.scopes = [];
  vault.epoch += 1;
}

/** Forget everything. Called when the link is dead and when the tab unloads. */
function clearVault(): void {
  vault.segment = "";
  dropCredentials();
}

/**
 * The single path segment, URL-decoded.
 *
 * A segment carrying a DOT is the shell itself (`/manage.html` on the dev
 * server, any asset path that reaches this entry) and is not a booking id — the
 * same rule `portal-main.tsx` applies to its slug, for the same reason: getting
 * it wrong turns a working dev server into a dead link.
 */
export function readSegment(): string {
  if (typeof window === "undefined") return "";
  const first = window.location.pathname.split("/").filter(Boolean)[0];
  if (first === undefined) return "";
  let decoded = first;
  try {
    decoded = decodeURIComponent(first);
  } catch {
    return "";
  }
  return decoded.includes(".") ? "" : decoded;
}

/* ── failures ────────────────────────────────────────────────────────── */

/**
 * The finished states of docket 0029 §"Failure paths", narrowed by 0038: for a
 * booking-id link an unknown id, another tenant's id and a garbage string all
 * answer the same `401 tokenInvalid`. `expired` and `revoked` survive for links
 * mailed before the cutover; `unreachable` is ours, not the link's.
 */
export type ManageFailure = "no-link" | "invalid" | "expired" | "revoked" | "unreachable";

function classify(error: unknown): ManageFailure {
  if (!isApiError(error)) return "unreachable";
  switch (error.code) {
    case "tokenExpired":
      return "expired";
    case "tokenRevoked":
      return "revoked";
    case "tokenInvalid":
    case "tokenSuperseded":
      return "invalid";
    default:
      // A 5xx, a network failure or an unregistered code is OUR problem, and
      // saying "this link is no longer valid" for it would send a customer to
      // support over a warming isolate.
      return "unreachable";
  }
}

/**
 * The answers that mean "the SESSION this page holds is no good", never "the
 * link is dead": no session sent, a session past its life, and a session of an
 * older generation than the booking now has (`requireSession` in the manage
 * worker answers `401 tokenSuperseded` for exactly that, and the typed client
 * keeps the wire code). All three are answered the same way: drop every
 * credential and offer the ordinary re-exchange from the stable segment.
 */
const SESSION_LOSS_CODES: ReadonlySet<string> = new Set([
  "sessionExpired",
  "sessionRequired",
  "tokenSuperseded",
]);

function isSessionLoss(error: unknown): boolean {
  return isApiError(error) && SESSION_LOSS_CODES.has(error.code);
}

/** Thrown by `run()` when the visitor dismissed the step-up. Never surfaced. */
export class StepUpAbandoned extends Error {
  constructor() {
    super("step-up abandoned");
    this.name = "StepUpAbandoned";
  }
}

/**
 * Thrown by `run()` when the session died mid-flow, when there is no session
 * to send at all (after a move, or after the tab was put away), or when an
 * answer or a caller belongs to a session, a lifetime or a render the page no
 * longer holds. Nothing is dispatched without a session, and nothing is retried
 * on a different one. It is never thrown in place of a successful answer.
 */
export class SessionLost extends Error {
  constructor() {
    super("manage session lost");
    this.name = "SessionLost";
  }
}

export function isAbandoned(error: unknown): boolean {
  return error instanceof StepUpAbandoned || error instanceof SessionLost;
}

/* ── the step-up gate ────────────────────────────────────────────────── */

/** The booking worker owns request/confirm; the server refusal names the scope. */
export const STEP_UP_ENABLED = true;

export interface StepUpRequest {
  /** The scope the SERVER named in its refusal — never one the page assumed. */
  scope: ManageElevationScope;
  /** Populated by `requestCode()`; the page shows only this. */
  maskedTarget: string;
  channel: "email" | "sms" | null;
  retryAfterSeconds: number;
  sending: boolean;
  /** A catalog key, resolved by the caller. Never an envelope `message`. */
  errorKey: string | null;
}

/** The step-up on screen, the authority it was opened for, and the hook lifetime that opened it. */
interface Gate {
  authority: number;
  life: object;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
}

/* ── phases ──────────────────────────────────────────────────────────── */

export type ManagePhase = "exchanging" | "ready" | "stale" | "dead";

/**
 * Why the page holds no session right now.
 *  - `"moved"`: the customer's own move SUCCEEDED and rotated the booking's
 *    generation. The page keeps the moved view and its notice.
 *  - `"session"`: a call came back as session loss (expired, missing, or an
 *    older generation), or the tab was put away and restored.
 */
export type StaleCause = "moved" | "session";

/** The re-exchange on offer while `phase === "stale"`; null in every other phase. */
export interface ManageRecovery {
  cause: StaleCause;
  /** A re-exchange is in flight. A second tap joins it. */
  pending: boolean;
  /** The last re-exchange failed on OUR side (network, 5xx); it can be tried again. */
  failed: boolean;
}

export interface ManageSessionApi {
  phase: ManagePhase;
  failure: ManageFailure | null;
  view: ManageViewDto | null;
  /** 0029: the exchange SUCCEEDS and returns a read-only tombstone. */
  superseded: boolean;
  /** JAI-2380: a staff member opened this page as a read-only preview. */
  staffPreview: boolean;
  elevated: boolean;
  scopes: readonly ManageElevationScope[];
  /** The pending step-up, or null. Rendered by `StepUpDialog`. */
  stepUp: StepUpRequest | null;
  /** Set exactly while `phase === "stale"`. */
  recovery: ManageRecovery | null;
  /**
   * Re-run the exchange. While stale this is `recover()`; otherwise (the
   * `unreachable` finished state) the full exchange the page opened with.
   */
  reopen: () => void;
  /**
   * The explicit way back from `'stale'`: the ordinary typed exchange from the
   * stable booking-id segment, single-flight (a second call joins the first).
   * Success installs the NEW generation's session, no elevation, and the view the
   * server returned with it. A failure on our side keeps the page as it was and
   * can be tried again; a refusal of the link itself is the finished state that
   * names it. An answer that arrives after `pagehide` or unmount is discarded,
   * and a call while the page is not stale does nothing.
   */
  recover: () => Promise<void>;
  /**
   * Call ONLY after the server answered a move with the moved booking: the move
   * rotated the booking's generation, so the session and any elevation this page
   * holds are already refused server-side. Drops them now, before anything else
   * can be sent on them, and moves the page to `'stale'` with cause `"moved"`.
   * Never call it for an unknown outcome, and never for a cancellation, an
   * answers edit or a reminder change: none of those rotate. Bound to the
   * session it was rendered with: called after a newer session has been
   * installed, it does nothing.
   */
  sessionRotated: () => void;
  /**
   * Replace the rendered view with the one a mutation returned. Does not rotate.
   * Bound to the session it was rendered with: a view returned on an older
   * session never replaces the view a newer one returned.
   */
  applyView: (view: ManageViewDto) => void;
  /** Re-read the masked view from the server (never after a mutation). */
  reload: () => Promise<void>;
  /**
   * Run one operation with the credentials it needs, honouring the server's
   * refusals: a `403 verificationRequired` opens the step-up for the scope the
   * server named and retries ONCE, on the same authority and lifetime only;
   * session loss moves the page to `'stale'`. With no session held, or from a
   * render whose session has been replaced, nothing is dispatched. A successful
   * answer is always handed back: it is never retried.
   */
  run: <T>(fn: (credentials: Credentials) => Promise<T>) => Promise<T>;
  /** Step-up dialog wiring, bound like the rest to the session it was rendered with. */
  requestCode: (channel?: "email" | "sms") => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  abandonStepUp: () => void;
  /** After elevation: swap the masked view for the full one (scope `pii:read`). */
  revealFull: () => Promise<void>;
}

/** What a successful exchange hands back, as the typed client declares it. */
type SessionData = Awaited<ReturnType<typeof manageSurface.createManageSession>>["data"];

/** One exchange (initial or recovery): its ticket, the authority and the lifetime it started under. */
interface ExchangeLease {
  ticket: object;
  epoch: number;
  life: object | null;
}

export function useManageSession(): ManageSessionApi {
  const [phase, setPhase] = useState<ManagePhase>("exchanging");
  const [failure, setFailure] = useState<ManageFailure | null>(null);
  const [view, setView] = useState<ManageViewDto | null>(null);
  const [superseded, setSuperseded] = useState(false);
  const [staffPreview, setStaffPreview] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [scopes, setScopes] = useState<readonly ManageElevationScope[]>([]);
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null);
  const [recovery, setRecovery] = useState<ManageRecovery | null>(null);
  /**
   * The GRANT: a fresh identity per installed session, null while none is held.
   * Every callback below closes over the value it was rendered with, and acts
   * only while that is still `grantRef.current`, so callbacks a component kept
   * from an older render cannot act on a newer session.
   */
  const [grant, setGrant] = useState<object | null>(null);
  const grantRef = useRef<object | null>(null);

  /** The step-up on screen and the promise `run()` parks on, for ONE authority and lifetime. */
  const gate = useRef<Gate | null>(null);
  /** The re-exchange in flight, joined by every further `recover()` until it settles. */
  const recoverFlight = useRef<{ ticket: object; promise: Promise<void> } | null>(null);
  /**
   * The exchange whose answer this hook will still accept. Replaced by every new
   * exchange; withdrawn on cleanup and on `pagehide`. An answer whose ticket is
   * not this one is never installed.
   */
  const exchangeTicket = useRef<object | null>(null);
  /**
   * This hook's LIFETIME: a fresh identity on every effect setup, withdrawn on
   * cleanup. Refs survive a StrictMode (or offscreen) cleanup/setup, so a boolean
   * would read "mounted" again for what is in fact a different lifetime; an
   * identity cannot. Every asynchronous continuation compares the identity it
   * captured with this one before it touches the vault, a gate or the state.
   */
  const lifetime = useRef<object | null>(null);
  /** The vault epoch at which THIS hook installed the session it holds, or null. */
  const ownedEpoch = useRef<number | null>(null);
  /** A session was installed and has not been dropped by this hook since. */
  const liveRef = useRef(false);
  /** The page is in `'stale'` (mirrors the state for synchronous readers). */
  const staleRef = useRef(false);

  /** Withdraw the grant: every callback rendered with it becomes inert. */
  const revokeGrant = useCallback(() => {
    grantRef.current = null;
    setGrant(null);
  }, []);

  const beginExchange = useCallback((): ExchangeLease => {
    const lease = { ticket: {}, epoch: vault.epoch, life: lifetime.current };
    exchangeTicket.current = lease.ticket;
    return lease;
  }, []);

  /** Is this exchange's answer still wanted: same lifetime, same ticket, same authority? */
  const ownsExchange = useCallback((lease: ExchangeLease): boolean => (
    lease.life !== null
    && lifetime.current === lease.life
    && exchangeTicket.current === lease.ticket
    && vault.epoch === lease.epoch
  ), []);

  const endExchange = useCallback((lease: ExchangeLease) => {
    if (exchangeTicket.current === lease.ticket) exchangeTicket.current = null;
  }, []);

  /** Install a freshly exchanged session: new generation, new grant, no elevation, the server's view. */
  const accept = useCallback((data: SessionData) => {
    vault.session = data.session_token;
    // A change of generation invalidates any elevation we were holding.
    vault.elevation = "";
    vault.scopes = [];
    vault.epoch += 1;
    ownedEpoch.current = vault.epoch;
    const issued = {};
    grantRef.current = issued;
    setGrant(issued);
    liveRef.current = true;
    staleRef.current = false;
    setElevated(false);
    setScopes([]);
    setSuperseded(data.superseded === true);
    setStaffPreview((data as { staff_preview?: boolean }).staff_preview === true);
    setView(data.view);
    setRecovery(null);
    setFailure(null);
    setPhase("ready");
  }, []);

  /** The link itself was refused: a finished state, nothing held. */
  const die = useCallback((reason: ManageFailure) => {
    clearVault();
    ownedEpoch.current = null;
    revokeGrant();
    liveRef.current = false;
    staleRef.current = false;
    setRecovery(null);
    setView(null);
    setFailure(reason);
    setPhase("dead");
  }, [revokeGrant]);

  /**
   * An exchange answered after it stopped being wanted. Its answer is dropped.
   * Only the lifetime that started it may react, and only if nothing else is in
   * flight and nothing is held: a stale page gets its button back, and a page
   * that was still opening (the tab was put away mid-exchange) gets the retry it
   * can act on. An exchange from an ended lifetime does nothing at all.
   */
  const settleOrphan = useCallback((lease: ExchangeLease) => {
    if (lease.life === null || lifetime.current !== lease.life) return;
    if (exchangeTicket.current !== null || liveRef.current) return;
    if (staleRef.current) {
      setRecovery((current) => (current ? { ...current, pending: false } : current));
      return;
    }
    die("unreachable");
  }, [die]);

  const exchange = useCallback(async (segment: string) => {
    const lease = beginExchange();
    setPhase("exchanging");
    setFailure(null);
    try {
      /* The page's first exchange was started at page open (manage-early, CC 0337); take it once. */
      const response = await (takeEarlyExchange(segment) ?? manageSurface.createManageSession({ token: segment }));
      if (!ownsExchange(lease)) {
        settleOrphan(lease);
        return;
      }
      endExchange(lease);
      accept(response.data);
    } catch (error) {
      if (!ownsExchange(lease)) {
        settleOrphan(lease);
        return;
      }
      endExchange(lease);
      die(classify(error));
    }
    // The URL is left exactly as the customer opened it, whatever the exchange
    // answered — including on a dead link, where the id in the address bar is
    // what lets them re-try, forward it, or read it out to support.
  }, [accept, die, beginExchange, ownsExchange, endExchange, settleOrphan]);

  /**
   * Every credential goes, at once, and the page moves to `'stale'`. The view is
   * KEPT: it is the last thing the server told this customer, and for a move it
   * is the moved booking. A step-up on screen belonged to the generation that
   * just ended, so it is closed and the call parked on it is told the session
   * was lost (it is never resumed on the next session).
   */
  const loseSession = useCallback((cause: StaleCause) => {
    dropCredentials();
    ownedEpoch.current = null;
    revokeGrant();
    liveRef.current = false;
    staleRef.current = true;
    setElevated(false);
    setScopes([]);
    const parked = gate.current;
    gate.current = null;
    setStepUp(null);
    parked?.reject(new SessionLost());
    setRecovery((current) => {
      if (!current) return { cause, pending: false, failed: false };
      return cause === "moved" ? { ...current, cause } : current;
    });
    setPhase("stale");
  }, [revokeGrant]);

  useEffect(() => {
    const life = {};
    lifetime.current = life;
    const segment = readSegment();
    if (!segment) {
      setFailure("no-link");
      setPhase("dead");
    } else {
      vault.segment = segment;
      void exchange(segment);
    }
    return () => {
      // This lifetime is over. Nothing it started, and no callback it handed
      // out, may install, open, resolve or clear anything from here on.
      if (lifetime.current === life) lifetime.current = null;
      revokeGrant();
      exchangeTicket.current = null;
      recoverFlight.current = null;
      // A step-up it opened is closed, and the call parked on it is released
      // with `SessionLost` rather than left waiting for ever.
      const parked = gate.current;
      gate.current = null;
      setStepUp(null);
      parked?.reject(new SessionLost());
      // The session it installed is dropped — but only if it is still the one in
      // the vault. A newer hook's session is never touched by this cleanup.
      if (ownedEpoch.current !== null && vault.epoch === ownedEpoch.current) dropCredentials();
      ownedEpoch.current = null;
      liveRef.current = false;
      staleRef.current = false;
    };
  }, [exchange, revokeGrant]);

  useEffect(() => {
    const forget = () => {
      // The tab is being put away: what it held is forgotten. A page that was
      // holding a session says so NOW, rather than keeping controls that can no
      // longer send anything: it moves to `'stale'` with its last view kept and
      // the explicit way back on offer. A page that was already stale (after a
      // known move, say) keeps its cause. Nothing is exchanged on restore.
      if (liveRef.current) loseSession("session");
      clearVault();
      ownedEpoch.current = null;
      // An exchange in flight now answers for a page that has been put away.
      exchangeTicket.current = null;
      if (recoverFlight.current) {
        recoverFlight.current = null;
        setRecovery((current) => (current ? { ...current, pending: false } : current));
      }
      // A step-up on screen was for the session just forgotten.
      const parked = gate.current;
      gate.current = null;
      setStepUp(null);
      parked?.reject(new SessionLost());
    };
    window.addEventListener("pagehide", forget);
    return () => window.removeEventListener("pagehide", forget);
  }, [loseSession]);

  const recover = useCallback((): Promise<void> => {
    // Single flight: a second tap, a rerender or a second caller joins the
    // exchange already in flight instead of starting another.
    if (recoverFlight.current) return recoverFlight.current.promise;
    // Only a stale page of a live lifetime recovers; a stale caller of a page
    // that has since recovered (or gone) does nothing. No grant is needed: a
    // stale page holds none, by definition.
    if (!staleRef.current || lifetime.current === null) return Promise.resolve();
    // The vault's copy is gone after `pagehide`; the address bar still has it.
    const segment = vault.segment || readSegment();
    if (!segment) {
      die("no-link");
      return Promise.resolve();
    }
    vault.segment = segment;
    const lease = beginExchange();
    setRecovery((current) =>
      current
        ? { ...current, pending: true, failed: false }
        : { cause: "session", pending: true, failed: false },
    );
    const promise = (async () => {
      try {
        const response = await manageSurface.createManageSession({ token: segment });
        if (!ownsExchange(lease)) {
          settleOrphan(lease);
          return;
        }
        endExchange(lease);
        accept(response.data);
      } catch (error) {
        if (!ownsExchange(lease)) {
          settleOrphan(lease);
          return;
        }
        endExchange(lease);
        const reason = classify(error);
        if (reason === "unreachable") {
          // Ours, not the link's (no connection, a 5xx, an unknown answer). The
          // page and the moved booking stay exactly as they were; the customer
          // can ask again. Nothing is retried on their behalf.
          setRecovery((current) => (current ? { ...current, pending: false, failed: true } : current));
          return;
        }
        // The server refused the link itself: the finished state that says so.
        die(reason);
      } finally {
        // Only this flight's own bookkeeping; a newer recovery's is never touched.
        if (recoverFlight.current?.ticket === lease.ticket) recoverFlight.current = null;
      }
    })();
    recoverFlight.current = { ticket: lease.ticket, promise };
    return promise;
  }, [accept, die, beginExchange, ownsExchange, endExchange, settleOrphan]);

  const reopen = useCallback(() => {
    if (staleRef.current) {
      void recover();
      return;
    }
    const segment = vault.segment || readSegment();
    if (!segment) {
      setFailure("invalid");
      setPhase("dead");
      return;
    }
    vault.segment = segment;
    void exchange(segment);
  }, [exchange, recover]);

  /**
   * Is the grant this callback was rendered with still the one in force, in a
   * live lifetime, AND is the authority this hook installed still the one in the
   * shared vault? The last condition is what makes a callback inert once ANY
   * other hook has installed a newer session, even while this hook is mounted
   * and still believes it holds its own.
   */
  const holds = (rendered: object | null): rendered is object =>
    rendered !== null
    && grantRef.current === rendered
    && lifetime.current !== null
    && ownedEpoch.current !== null
    && ownedEpoch.current === vault.epoch;

  const sessionRotated = useCallback(() => {
    // Only the session this callback was rendered with can be the one the move
    // rotated. Called later, from a render a newer session has replaced or from
    // a lifetime that has ended, it drops nothing.
    if (!holds(grant)) return;
    loseSession("moved");
  }, [grant, loseSession]);

  const applyView = useCallback((next: ManageViewDto) => {
    // A view returned on an older session never replaces a newer session's view.
    if (!holds(grant)) return;
    setView(next);
    setSuperseded(false);
  }, [grant]);

  const openGate = useCallback((scope: ManageElevationScope, authority: number, life: object) => {
    /* A rerender or a second refused request on the SAME authority and lifetime
       joins the verification already on screen. Replacing its resolver strands
       the first caller forever. A gate left over from another authority or
       lifetime is closed. */
    const open = gate.current;
    if (open && open.authority === authority && open.life === life) return open.promise;
    if (open) {
      gate.current = null;
      open.reject(new SessionLost());
    }

    let resolveGate!: () => void;
    let rejectGate!: (error: unknown) => void;
    const promise = new Promise<void>((resolve, reject) => {
      resolveGate = resolve;
      rejectGate = reject;
    });
    gate.current = { authority, life, promise, resolve: resolveGate, reject: rejectGate };
    setStepUp({
      scope,
      maskedTarget: "",
      channel: null,
      retryAfterSeconds: 0,
      sending: false,
      errorKey: null,
    });
    return promise;
  }, []);

  const run = useCallback(
    async <T,>(fn: (credentials: Credentials) => Promise<T>): Promise<T> => {
      /** The lifetime this operation belongs to, for its whole life. */
      const life = lifetime.current;
      if (life === null) throw new SessionLost();
      // A caller holding a render whose session has been replaced — by this hook
      // or by another one sharing the vault — or that never had one, dispatches
      // nothing. (`pagehide` moves a live page to `'stale'` itself, so a page
      // restored from the back/forward cache is never left here with dead controls.)
      if (!holds(grant)) throw new SessionLost();
      if (!vault.session) {
        if (liveRef.current) loseSession("session");
        throw new SessionLost();
      }
      /** The authority this operation was asked under, for its whole life. */
      const authority = vault.epoch;
      const ownLife = () => lifetime.current === life;
      const stillHeld = () => ownLife() && vault.epoch === authority && vault.session !== "";
      /** One dispatch with the credentials of THIS authority, or none at all. */
      const dispatch = async (): Promise<T> => {
        if (!stillHeld()) throw new SessionLost();
        try {
          return await fn(credentials());
        } catch (error) {
          if (isSessionLoss(error)) {
            // Not a dead link: the generation rotated, or the 30-minute session
            // ran out. An answer to credentials that have ALREADY been replaced,
            // or that reaches a lifetime that has ended, changes nothing.
            if (ownLife() && vault.epoch === authority) loseSession("session");
            throw new SessionLost();
          }
          throw error;
        }
      };
      try {
        return await dispatch();
      } catch (error) {
        if (!STEP_UP_ENABLED || !isApiError(error) || error.code !== "verificationRequired") throw error;
        // A refusal for an authority or a lifetime this page no longer holds
        // opens no step-up, clears nothing on what replaced it, and is never
        // replayed.
        if (!stillHeld()) throw new SessionLost();
        // The SERVER names the scope; the page never assumes one. A refusal
        // without a scope detail still runs the step-up — the elevation the mock
        // and the worker mint covers every scope, so the retry is what decides.
        const named = (error.details as { scope?: unknown } | undefined)?.scope;
        const scope = (typeof named === "string" ? named : "pii:read") as ManageElevationScope;
        // A booking change invalidates elevation; drop what we hold so the retry
        // cannot send a token the server has already refused.
        vault.elevation = "";
        vault.scopes = [];
        setElevated(false);
        setScopes([]);
        await openGate(scope, authority, life);
        // The code was confirmed for THIS authority and lifetime, or the retry
        // does not happen.
        if (!stillHeld()) throw new SessionLost();
        return dispatch();
      }
    },
    [grant, openGate, loseSession],
  );

  const requestCode = useCallback(async (channel?: "email" | "sms") => {
    // A code is only ever requested on the customer's tap, for the step-up on
    // screen, on the session and the lifetime held now.
    if (!holds(grant)) return;
    const owner = gate.current;
    if (!owner || owner.life !== lifetime.current || !vault.session || owner.authority !== vault.epoch) return;
    const epoch = vault.epoch;
    const stillOwner = () => gate.current === owner && lifetime.current === owner.life && vault.epoch === epoch;
    setStepUp((current) => (current ? { ...current, sending: true, errorKey: null } : current));
    try {
      const response = await manageSurface.requestManageVerification(
        credentials(),
        channel ? { channel } : {},
      );
      // An answer for a step-up that is no longer on screen updates nothing.
      if (!stillOwner()) return;
      const data = response.data;
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            channel: data.channel,
            // Only ever the masked target. The page has not proven who this is.
            maskedTarget: data.masked_target,
            retryAfterSeconds: data.retry_after_seconds,
            errorKey: null,
          }
          : current,
      );
    } catch (error) {
      if (!stillOwner()) return;
      const code = isApiError(error) ? error.code : "";
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            errorKey:
              code === "verificationUnavailable"
                ? "verify.unavailable"
                : code === "otpExhausted"
                  ? "verify.exhausted"
                  : "error.generic",
          }
          : current,
      );
    }
  }, [grant]);

  const submitCode = useCallback(async (code: string) => {
    if (!holds(grant)) return;
    const owner = gate.current;
    if (!owner || owner.life !== lifetime.current || !vault.session || owner.authority !== vault.epoch) return;
    const epoch = vault.epoch;
    const stillOwner = () => gate.current === owner && lifetime.current === owner.life && vault.epoch === epoch;
    setStepUp((current) => (current ? { ...current, sending: true, errorKey: null } : current));
    try {
      const response = await manageSurface.confirmManageVerification(credentials(), { code });
      // An elevation minted for a step-up, a session or a lifetime this page no
      // longer holds is never kept, and it resolves nothing.
      if (!stillOwner()) return;
      vault.elevation = response.data.elevated_token;
      vault.scopes = response.data.scopes;
      setElevated(true);
      setScopes(response.data.scopes);
      setStepUp(null);
      gate.current = null;
      owner.resolve();
    } catch (error) {
      if (!stillOwner()) return;
      const apiCode = isApiError(error) ? error.code : "";
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            errorKey: apiCode === "otpExhausted" ? "verify.exhausted" : "verify.error",
          }
          : current,
      );
    }
  }, [grant]);

  const abandonStepUp = useCallback(() => {
    // Only this session's step-up can be dismissed by this render's control.
    if (!holds(grant)) return;
    setStepUp(null);
    gate.current?.reject(new StepUpAbandoned());
    gate.current = null;
  }, [grant]);

  const reload = useCallback(async () => {
    const life = lifetime.current;
    if (life === null || !holds(grant)) return;
    if (!vault.session) {
      if (liveRef.current) loseSession("session");
      return;
    }
    const epoch = vault.epoch;
    const current = () => lifetime.current === life && vault.epoch === epoch;
    try {
      const response = await manageSurface.getManageView(credentials());
      if (!current()) return;
      setView(response.data);
    } catch (error) {
      if (isSessionLoss(error)) {
        if (current()) loseSession("session");
        return;
      }
      throw error;
    }
  }, [grant, loseSession]);

  const revealFull = useCallback(async () => {
    const life = lifetime.current;
    const authority = vault.epoch;
    const response = await run((c) => manageSurface.getManageViewFull(c));
    // A full view read on a session or a lifetime this page no longer holds is
    // never laid over the view the newer one returned.
    if (lifetime.current !== life || vault.epoch !== authority) return;
    setView(response.data);
  }, [run]);

  return {
    phase,
    failure,
    view,
    superseded,
    staffPreview,
    elevated,
    scopes,
    stepUp,
    recovery,
    reopen,
    recover,
    sessionRotated,
    applyView,
    reload,
    run,
    requestCode,
    submitCode,
    abandonStepUp,
    revealFull,
  };
}

/* ── mutation envelopes ──────────────────────────────────────────────── */

/**
 * ONE key per user INTENT, minted when the intent BEGINS — the moment the
 * confirm dialog opens, not the moment the button is tapped — and reused by
 * every attempt of that intent, including the one the visitor causes by tapping
 * twice. That is the whole of acceptance item 7, which is why each dialog mints
 * its key in a `useMemo` keyed on its own mount rather than in its submit
 * handler.
 *
 * It is deliberately NOT re-minted after a `409 bookingVersionConflict`: the
 * re-confirm is the same intent, and the server stored nothing for the key when
 * it refused, so the second attempt is the first one that can succeed.
 */

/** `view.version`, echoed verbatim. Never re-derived, never defaulted. */
export function envelopeOf(view: ManageViewDto, key: string) {
  return { idempotency_key: key, version: view.version };
}
